import { Pause, Play, Square, Trash2 } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "../../../components/ui/button/index.js";
import { cn } from "../../../lib/utils.js";
import { DecorativeWaveform } from "../DecorativeWaveform/DecorativeWaveform.js";
import { formatMediaClock, mediaProgressRatio } from "../media-clock.js";
import { pauseHtmlAudio, playHtmlAudio, AUDIO_VISUALLY_HIDDEN_CLASS } from "../play-html-audio.js";

export function ComposerVoicePreview({
  previewUrl,
  durationMs,
  recording,
  liveWaveformBarHeights = null,
  liveWaveformProgress = 0,
  removeLabel,
  onRemove,
  onStopRecording,
}: {
  previewUrl: string | null;
  durationMs: number;
  recording: boolean;
  liveWaveformBarHeights?: readonly number[] | null;
  liveWaveformProgress?: number;
  removeLabel: string;
  onRemove: () => void;
  onStopRecording: () => void;
}): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [resolvedDuration, setResolvedDuration] = useState(durationMs / 1000);
  const isRtl = i18n.dir() === "rtl";

  useEffect(() => {
    setPlaying(false);
    setCurrentTime(0);
    setResolvedDuration(durationMs / 1000);
    const node = audioRef.current;
    if (node) {
      pauseHtmlAudio(node);
      node.currentTime = 0;
    }
  }, [previewUrl, recording, durationMs]);

  useEffect(() => {
    return () => {
      const node = audioRef.current;
      if (node) {
        pauseHtmlAudio(node);
      }
    };
  }, []);

  const togglePlay = useCallback(async () => {
    const node = audioRef.current;
    if (!node || recording) {
      return;
    }
    if (playing) {
      pauseHtmlAudio(node);
      setPlaying(false);
      return;
    }
    const result = await playHtmlAudio(node);
    setPlaying(result.status === "played");
  }, [playing, recording]);

  const progress = recording
    ? liveWaveformProgress
    : mediaProgressRatio(currentTime, resolvedDuration > 0 ? resolvedDuration : durationMs / 1000);
  const elapsedLabel = formatMediaClock(recording ? durationMs / 1000 : currentTime);
  const durationLabel = recording
    ? formatMediaClock(durationMs / 1000)
    : formatMediaClock(resolvedDuration > 0 ? resolvedDuration : durationMs / 1000);

  return (
    <div
      className="relative my-0.5 flex w-full max-w-full min-w-0 items-center gap-2 rounded-xl border border-line/80 bg-surface-muted/60 px-2 py-1.5"
      data-testid="composer-voice-preview"
      aria-live="polite"
    >
      {previewUrl && !recording ? (
        <audio
          ref={audioRef}
          src={previewUrl}
          preload="metadata"
          className={AUDIO_VISUALLY_HIDDEN_CLASS}
          onLoadedMetadata={(event) => {
            const duration = event.currentTarget.duration;
            if (Number.isFinite(duration) && duration > 0) {
              setResolvedDuration(duration);
            }
          }}
          onTimeUpdate={(event) => {
            setCurrentTime(event.currentTarget.currentTime);
          }}
          onEnded={() => setPlaying(false)}
          onPause={() => setPlaying(false)}
        />
      ) : null}

      {recording ? (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="size-9 shrink-0 rounded-full bg-destructive/10 p-0 text-destructive hover:bg-destructive/15"
          aria-label={t("inbox.composer.voiceRecorder.stop")}
          onClick={onStopRecording}
        >
          <Square size={14} aria-hidden="true" />
        </Button>
      ) : (
        <button
          type="button"
          className={cn(
            "flex size-9 shrink-0 items-center justify-center rounded-full",
            "bg-accent text-accent-contrast shadow-sm",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus",
          )}
          aria-label={
            playing
              ? t("inbox.composer.voiceRecorder.pausePreview")
              : t("inbox.composer.voiceRecorder.playPreview")
          }
          onClick={() => void togglePlay()}
        >
          {playing ? (
            <Pause className="size-4 fill-current" aria-hidden="true" />
          ) : (
            <Play className="size-4 translate-x-px fill-current" aria-hidden="true" />
          )}
        </button>
      )}

      <div className="min-w-0 flex-1">
        <DecorativeWaveform
          progress={progress}
          isRtl={isRtl}
          barHeights={recording ? (liveWaveformBarHeights ?? undefined) : undefined}
          className="h-7 w-full max-w-none"
        />
        <span className="mt-0.5 flex justify-between text-[10px] tabular-nums text-ink-subtle">
          <span>{elapsedLabel}</span>
          <span>{durationLabel}</span>
        </span>
      </div>

      {!recording ? (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="size-8 shrink-0 rounded-full p-0 text-ink-muted hover:bg-surface hover:text-ink"
          aria-label={removeLabel}
          onClick={onRemove}
        >
          <Trash2 size={15} aria-hidden="true" />
        </Button>
      ) : null}
    </div>
  );
}
